import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { LeadRegistrationDialog } from './LeadRegistrationDialog';

/**
 * Wraps children and renders a blurred gate with a CTA when the user is not
 * verified (lead) nor admin. Admins and verified leads see the content directly.
 */
export function LeadGate({ children, title = 'Desbloquea tus Resultados Estimados', description, active = true }) {
  const { isUnlocked } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (isUnlocked || !active) return <>{children}</>;

  return (
    <div className="relative" data-testid="lead-gate">
      {/* Blurred preview of children */}
      <div className="pointer-events-none select-none blur-md opacity-60 overflow-hidden max-h-[420px]" aria-hidden="true">
        {children}
      </div>

      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute inset-0 flex items-center justify-center p-4"
      >
        <div className="max-w-md w-full bg-white/95 backdrop-blur-sm rounded-2xl border-2 border-[#1A56DB] shadow-2xl shadow-blue-500/20 p-6 md:p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1A56DB] to-[#3B82F6] shadow-lg mb-4">
            <Lock className="h-6 w-6 text-white" />
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">{title}</h3>
          <p className="text-sm text-slate-600 mb-6 leading-relaxed">
            {description || 'Registra tu correo corporativo y te enviaremos un código para acceder a la estimación completa del presupuesto.'}
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="w-full h-12 bg-gradient-to-r from-[#1A56DB] to-[#3B82F6] text-white font-semibold gap-2"
            data-testid="lead-gate-unlock-button"
          >
            <Sparkles className="h-4 w-4" />
            Desbloquear resultados
          </Button>
          <p className="text-[11px] text-slate-400 mt-3">
            Solo aceptamos correos corporativos. 100% gratuito.
          </p>
        </div>
      </motion.div>

      <LeadRegistrationDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
